import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  FormControlLabel,
  Chip,
} from "@mui/material";

const initialForm = {
  nombre: "",
  tipo: "DESCUENTO_CATEGORIA",
  prioridad: 50,
  porcentaje: "",
  categoria: "",
  minCantidad: "2",
  minDiasSinVenta: "30",
  categoriasCombo: "TOPS,JEANS",
  montoFijo: "",
  margenMinimoPct: "0",
  activa: true,
};

export default function PromocionesPrecio({ store }) {
  const [form, setForm] = useState(initialForm);
  const [feedback, setFeedback] = useState(null);

  const promociones = store.promociones || [];
  const categoriasDisponibles = useMemo(() => {
    const values = (store.productos || [])
      .map((p) => String(p.categoria || "").trim())
      .filter(Boolean)
      .map((c) => c.toUpperCase());
    return [...new Set(values)].sort((a, b) =>
      a.localeCompare(b, "es", { sensitivity: "base" })
    );
  }, [store.productos]);
  const categoriasSet = useMemo(
    () => new Set(categoriasDisponibles),
    [categoriasDisponibles]
  );

  const promoStats = useMemo(() => {
    const activas = promociones.filter((p) => p.activa).length;
    return { total: promociones.length, activas };
  }, [promociones]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const buildRules = () => {
    if (form.tipo === "DESCUENTO_CATEGORIA") {
      return {
        categoria: String(form.categoria || "").trim().toUpperCase(),
        porcentaje: parseFloat(form.porcentaje) || 0,
      };
    }
    if (form.tipo === "DESCUENTO_VOLUMEN") {
      return {
        minCantidad: parseInt(form.minCantidad) || 2,
        porcentaje: parseFloat(form.porcentaje) || 0,
      };
    }
    if (form.tipo === "ROTACION_LENTA") {
      return {
        minDiasSinVenta: parseInt(form.minDiasSinVenta) || 30,
        porcentaje: parseFloat(form.porcentaje) || 0,
      };
    }
    if (form.tipo === "COMBO_OUTFIT") {
      return {
        categorias: String(form.categoriasCombo || "")
          .split(",")
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean),
        porcentaje: parseFloat(form.porcentaje) || 0,
        montoFijo: parseFloat(form.montoFijo) || 0,
      };
    }
    return {};
  };

  const validar = () => {
    if (!form.nombre.trim()) return "Nombre de promoción es obligatorio";
    if (form.tipo === "DESCUENTO_CATEGORIA") {
      if (!form.categoria.trim()) return "Debes seleccionar una categoría";
      if (categoriasDisponibles.length === 0) {
        return "No hay categorías disponibles en inventario";
      }
      if (!categoriasSet.has(String(form.categoria).trim().toUpperCase())) {
        return "La categoría debe existir en inventario";
      }
    }
    if (["DESCUENTO_CATEGORIA", "DESCUENTO_VOLUMEN", "ROTACION_LENTA"].includes(form.tipo)) {
      if (!form.porcentaje || parseFloat(form.porcentaje) <= 0) {
        return "Porcentaje debe ser mayor a 0";
      }
    }
    if (form.tipo === "COMBO_OUTFIT") {
      const comboCats = String(form.categoriasCombo || "")
        .split(",")
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);
      if (comboCats.length === 0) {
        return "Debes seleccionar categorías existentes para el combo";
      }
      const invalidas = comboCats.filter((c) => !categoriasSet.has(c));
      if (invalidas.length > 0) {
        return `Categorías no válidas en combo: ${invalidas.join(", ")}`;
      }
      const pct = parseFloat(form.porcentaje) || 0;
      const fijo = parseFloat(form.montoFijo) || 0;
      if (pct <= 0 && fijo <= 0) {
        return "Combo outfit requiere porcentaje o monto fijo";
      }
    }
    return null;
  };

  const guardar = async () => {
    const err = validar();
    if (err) {
      setFeedback({ tipo: "error", msg: err });
      return;
    }

    try {
      const id = await store.guardarPromocion({
        nombre: form.nombre,
        tipo: form.tipo,
        prioridad: parseInt(form.prioridad) || 50,
        activa: form.activa,
        reglas: buildRules(),
        guardias: {
          margenMinimoPct: parseFloat(form.margenMinimoPct) || 0,
        },
        canales: ["TIENDA"],
      });

      if (!id) {
        setFeedback({ tipo: "error", msg: "No se pudo guardar la promocion" });
        return;
      }

      setFeedback({ tipo: "success", msg: "Promocion guardada" });
      setForm(initialForm);
    } catch (error) {
      const serverMsg = String(error?.response?.data?.message || "").trim();
      const errorMsg = String(error?.message || "").trim();
      setFeedback({
        tipo: "error",
        msg: serverMsg || errorMsg || "Error al guardar la promocion",
      });
    }
  };

  const togglePromo = async (promo) => {
    try {
      const id = await store.guardarPromocion({ ...promo, activa: !promo.activa });
      if (!id) {
        setFeedback({ tipo: "error", msg: "No se pudo actualizar la promocion" });
      }
    } catch (error) {
      const serverMsg = String(error?.response?.data?.message || "").trim();
      const errorMsg = String(error?.message || "").trim();
      setFeedback({
        tipo: "error",
        msg: serverMsg || errorMsg || "Error al actualizar la promocion",
      });
    }
  };

  const renderPromoConfig = (promo) => {
    const reglas = promo?.reglas || {};
    const guardias = promo?.guardias || {};
    const canales = Array.isArray(promo?.canales) ? promo.canales : [];

    const reglasDetalle = [];
    if (promo?.tipo === "DESCUENTO_CATEGORIA") {
      reglasDetalle.push(`Categoría: ${reglas.categoria || "N/D"}`);
      reglasDetalle.push(`Descuento: ${parseFloat(reglas.porcentaje || 0).toFixed(2)}%`);
    } else if (promo?.tipo === "DESCUENTO_VOLUMEN") {
      reglasDetalle.push(`Mín. unidades: ${parseInt(reglas.minCantidad) || 0}`);
      reglasDetalle.push(`Descuento: ${parseFloat(reglas.porcentaje || 0).toFixed(2)}%`);
    } else if (promo?.tipo === "ROTACION_LENTA") {
      reglasDetalle.push(`Mín. días sin venta: ${parseInt(reglas.minDiasSinVenta) || 0}`);
      reglasDetalle.push(`Descuento: ${parseFloat(reglas.porcentaje || 0).toFixed(2)}%`);
    } else if (promo?.tipo === "COMBO_OUTFIT") {
      const categorias = Array.isArray(reglas.categorias)
        ? reglas.categorias.filter(Boolean).join(", ")
        : "N/D";
      reglasDetalle.push(`Categorías: ${categorias || "N/D"}`);
      reglasDetalle.push(`Descuento %: ${parseFloat(reglas.porcentaje || 0).toFixed(2)}%`);
      reglasDetalle.push(`Monto fijo: ${parseFloat(reglas.montoFijo || 0).toFixed(2)}`);
    }

    if (reglasDetalle.length === 0) {
      const entries = Object.entries(reglas)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`);
      reglasDetalle.push(...entries);
    }

    return (
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          Reglas:
        </Typography>
        {reglasDetalle.length > 0 ? (
          reglasDetalle.map((item) => (
            <Typography key={item} variant="body2">
              {item}
            </Typography>
          ))
        ) : (
          <Typography variant="body2">Sin reglas</Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Guardias:
        </Typography>
        <Typography variant="body2">
          Margen mínimo: {parseFloat(guardias.margenMinimoPct || 0).toFixed(2)}%
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Canales:
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(canales.length > 0 ? canales : ["TIENDA"]).map((canal) => (
            <Chip key={canal} size="small" label={String(canal).toUpperCase()} variant="outlined" />
          ))}
        </Stack>
      </Stack>
    );
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={2}>
        Motor de Promociones y Precios
      </Typography>

      {feedback && (
        <Alert severity={feedback.tipo} sx={{ mb: 2 }}>
          {feedback.msg}
        </Alert>
      )}

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary">Promociones</Typography>
              <Typography variant="h4" fontWeight={800}>{promoStats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary">Activas</Typography>
              <Typography variant="h4" color="success.main" fontWeight={800}>{promoStats.activas}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography fontWeight={700} mb={2}>Nueva promoción</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Nombre"
                value={form.nombre}
                onChange={(e) => setField("nombre", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Select
                size="small"
                fullWidth
                value={form.tipo}
                onChange={(e) => setField("tipo", e.target.value)}
              >
                <MenuItem value="DESCUENTO_CATEGORIA">Descuento categoría</MenuItem>
                <MenuItem value="DESCUENTO_VOLUMEN">Descuento volumen</MenuItem>
                <MenuItem value="ROTACION_LENTA">Rotación lenta</MenuItem>
                <MenuItem value="COMBO_OUTFIT">Combo outfit</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Prioridad"
                type="number"
                size="small"
                fullWidth
                value={form.prioridad}
                onChange={(e) => setField("prioridad", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Margen mínimo %"
                type="number"
                size="small"
                fullWidth
                value={form.margenMinimoPct}
                onChange={(e) => setField("margenMinimoPct", e.target.value)}
              />
            </Grid>

            {form.tipo === "DESCUENTO_CATEGORIA" && (
              <>
                <Grid item xs={12} md={4}>
                  <Select
                    size="small"
                    fullWidth
                    value={form.categoria}
                    onChange={(e) => setField("categoria", e.target.value)}
                    displayEmpty
                    disabled={categoriasDisponibles.length === 0}
                  >
                    <MenuItem value="" disabled>
                      {categoriasDisponibles.length === 0
                        ? "Sin categorías en inventario"
                        : "Selecciona categoría"}
                    </MenuItem>
                    {categoriasDisponibles.map((categoria) => (
                      <MenuItem key={categoria} value={categoria}>
                        {categoria}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Porcentaje %"
                    type="number"
                    size="small"
                    fullWidth
                    value={form.porcentaje}
                    onChange={(e) => setField("porcentaje", e.target.value)}
                  />
                </Grid>
              </>
            )}

            {form.tipo === "DESCUENTO_VOLUMEN" && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Mínimo unidades"
                    type="number"
                    size="small"
                    fullWidth
                    value={form.minCantidad}
                    onChange={(e) => setField("minCantidad", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Porcentaje %"
                    type="number"
                    size="small"
                    fullWidth
                    value={form.porcentaje}
                    onChange={(e) => setField("porcentaje", e.target.value)}
                  />
                </Grid>
              </>
            )}

            {form.tipo === "ROTACION_LENTA" && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Días sin venta"
                    type="number"
                    size="small"
                    fullWidth
                    value={form.minDiasSinVenta}
                    onChange={(e) => setField("minDiasSinVenta", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Porcentaje %"
                    type="number"
                    size="small"
                    fullWidth
                    value={form.porcentaje}
                    onChange={(e) => setField("porcentaje", e.target.value)}
                  />
                </Grid>
              </>
            )}

            {form.tipo === "COMBO_OUTFIT" && (
              <>
                <Grid item xs={12} md={6}>
                  <Select
                    size="small"
                    fullWidth
                    multiple
                    value={String(form.categoriasCombo || "")
                      .split(",")
                      .map((c) => c.trim().toUpperCase())
                      .filter(Boolean)}
                    onChange={(e) => {
                      const value = Array.isArray(e.target.value)
                        ? e.target.value
                        : [e.target.value];
                      setField("categoriasCombo", value.join(","));
                    }}
                    displayEmpty
                    disabled={categoriasDisponibles.length === 0}
                  >
                    <MenuItem value="" disabled>
                      {categoriasDisponibles.length === 0
                        ? "Sin categorías en inventario"
                        : "Selecciona categorías del combo"}
                    </MenuItem>
                    {categoriasDisponibles.map((categoria) => (
                      <MenuItem key={categoria} value={categoria}>
                        {categoria}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Porcentaje %"
                    type="number"
                    size="small"
                    fullWidth
                    value={form.porcentaje}
                    onChange={(e) => setField("porcentaje", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Monto fijo"
                    type="number"
                    size="small"
                    fullWidth
                    value={form.montoFijo}
                    onChange={(e) => setField("montoFijo", e.target.value)}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.activa}
                    onChange={(e) => setField("activa", e.target.checked)}
                  />
                }
                label="Promoción activa"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="contained" onClick={() => void guardar()}>Guardar promoción</Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography fontWeight={700} mb={1}>Promociones configuradas</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Prioridad</TableCell>
                  <TableCell>Configuración</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promociones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">Sin promociones configuradas</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  [...promociones]
                    .sort((a, b) => (parseInt(a.prioridad) || 50) - (parseInt(b.prioridad) || 50))
                    .map((promo) => (
                      <TableRow key={promo.id} hover>
                        <TableCell>{promo.nombre}</TableCell>
                        <TableCell>{promo.tipo}</TableCell>
                        <TableCell>{promo.prioridad}</TableCell>
                        <TableCell>{renderPromoConfig(promo)}</TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={promo.activa !== false}
                                onChange={() => void togglePromo(promo)}
                              />
                            }
                            label={promo.activa !== false ? "Activa" : "Inactiva"}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
